import { Suspense, useRef, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import { maskDecimal, maskWholeNumber, unmaskNumber } from '@givebutter/util';
import { faPlus, faQuestionCircle, faRefresh, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    ItemRequest,
    MediaUpload,
    createCampaignItem,
    fetchCampaignCustomFields,
    fetchCampaignItems,
    updateCampaignItem,
} from 'api/campaigns';
import * as BundleOnlyConfirmationDialog from 'assets/js/pages/campaign-settings/campaign-event/BundleOnlyConfirmationDialog';
import * as CampaignItemForm from 'assets/js/pages/campaign-settings/campaign-event/CampaignItemForm';
import * as TicketCustomFieldForm from 'assets/js/pages/campaign-settings/campaign-event/TicketCustomFieldForm';
import { TicketFormHeader } from 'assets/js/pages/campaign-settings/campaign-event/TicketFormHeader';
import {
    TicketCustomFieldFormValues,
    TicketFormData,
} from 'assets/js/pages/campaign-settings/campaign-event/types';
import { useCurrentTicket } from 'assets/js/pages/campaign-settings/campaign-event/useCurrentTicket';
import * as CustomFieldForm from 'components/custom-fields';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { InputCurrencyIcon } from 'components/input';
import * as Tooltip from 'components/tooltip';
import { Upload, UploadFile, UploadRef } from 'components/upload';
import * as Wysiwyg from 'components/wysiwyg';
import { UseFormWysiwyg } from 'components/wysiwyg/UseFormWysiwyg';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { useCurrentCampaignEvent } from 'customHooks/useCurrentCampaignEvent';
import { useIsPending } from 'customHooks/useIsPending';
import { FilePondFile } from 'filepond';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { isEmpty } from 'lodash';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { twMerge } from 'tailwind-merge';
import { AuctionItem, CampaignEvent, CampaignItemBundle, CustomField } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface AuctionItemWithCustomFields extends AuctionItem {
    custom_fields?: CustomField[];
}

const EDITOR_MIN_HEIGHT = 200;

const TicketFormDrawer = () => {
    const toast = useToast();
    const { taxDeductibilitySettings } = useFlags();
    const { goBack } = useDialogRoute();
    const queryClient = useQueryClient();
    const ref = useRef<UploadRef>(null);
    const [files, setFiles] = useState<FilePondFile[]>([]);
    const { data: campaign } = useCurrentCampaign();
    const campaignId = campaign!.id;
    const [isBundleOnlyDialogOpen, setIsBundleOnlyDialogOpen] = useState(false);
    const { data: event } = useCurrentCampaignEvent();
    const { data: ticket } = useCurrentTicket();
    const isEditing = !!ticket;
    const drawerTitle = isEditing ? 'Update' : 'New';

    const { isBeta } = useCurrentAccount();
    const aiEnabled = isBeta;

    const { data: possibleCustomFields } = useSuspenseQuery({
        queryKey: campaignQueryKeys.customFieldsFiltered(campaignId, {
            group: 'ticket',
        }),
        queryFn: () =>
            fetchCampaignCustomFields(campaignId, {
                group: 'ticket',
            }),
    });

    const [isEventBundlesPending, eventBundleParams] = useIsPending(
        {
            is_bundle: true,
        },
        500,
    );

    const { data: eventBundles } = useSuspenseQuery({
        queryKey: campaignQueryKeys.itemsFiltered(campaign.id, eventBundleParams),
        queryFn: () => fetchCampaignItems({ campaignId: campaign.id, ...eventBundleParams }),
    });

    const totalBundles = eventBundles.meta.total;
    const customFields = possibleCustomFields ?? [];

    const formMethods = Form.useGivebutterForm<TicketFormData>({
        defaultValues: defaultForm(event.type, ticket as CampaignItemBundle, customFields),
    });

    const {
        control,
        handleSubmit,
        reset,
        resetFormButton,
        setFocus,
        setError,
        setValue,
        watch,
        path,
    } = formMethods;

    const [isFree, isUnlimited, pictures, bundleOnly, totalQuantity] = watch([
        'free',
        'unlimited',
        'pictures',
        'bundleOnly',
        'total_quantity',
    ]);

    const shouldShowQuantity = !isUnlimited || bundleOnly;
    const hasTicketCustomFields = !ticket || ticket.type === 'ticket';

    const quantityRequiredText = !isUnlimited && !bundleOnly;

    const isFreeText = !isFree && !bundleOnly ? 'Must provide a price or choose "Free".' : false;
    const arePicturesEmpty = pictures.length === 0;
    const [shouldExitAfterCreate, setShouldExitAfterCreate] = useState(true);

    const hasBundleData = Boolean(eventBundles.data.length);
    const [shouldShowBundleInfo, setShouldShowBundleInfo] = useState(hasBundleData);

    const invalidate = async () => {
        await queryClient.invalidateQueries({
            queryKey: campaignQueryKeys.items(campaignId),
            type: 'all',
            exact: false,
        });
        await queryClient.invalidateQueries({
            queryKey: campaignQueryKeys.item(campaignId, Number(ticket?.id)),
        });

        await queryClient.invalidateQueries({
            queryKey: campaignQueryKeys.customFieldsFiltered(campaignId, {
                group: 'ticket',
            }),
        });
    };

    const { mutate: createTicket, isPending: isCreating } = useMutation({
        mutationFn: (data: ItemRequest) => {
            return createCampaignItem({ campaignId: campaign.id, data, type: 'ticket' });
        },
        onSuccess: async (data: AuctionItemWithCustomFields) => {
            const updatedFields = data.custom_fields ?? customFields;
            toast.success('Ticket created.');
            await invalidate();
            if (shouldExitAfterCreate) {
                goBack();
            } else {
                reset({
                    ...defaultForm(
                        event.type,
                        ticket as CampaignItemBundle,
                        updatedFields.filter(field => !!field.group),
                    ),
                });
                setFocus('name');
            }
        },
        onError: (error: GivebutterAxiosError) => {
            const data = error.response?.data;
            const errors = data?.errors;

            if (typeof errors === 'object') {
                for (const field in errors) {
                    setError(field as keyof TicketFormData, {
                        type: 'server',
                        message: errors[field][0],
                    });
                }
            }

            toast.error(data?.message ?? 'Unable to create ticket.');
            resetFormButton();
        },
    });

    const { mutate: updateTicket, isPending: isUpdating } = useMutation({
        mutationFn: (data: ItemRequest) =>
            updateCampaignItem({ campaignId: campaign.id, itemId: Number(ticket?.id), data }),
        onSuccess: async () => {
            toast.success('Ticket updated.');
            await invalidate();
            goBack();
        },
        onError: (error: GivebutterAxiosError) => {
            const data = error.response?.data;
            const errors = data?.errors;

            if (typeof errors === 'object') {
                for (const field in errors) {
                    setError(field as keyof TicketFormData, {
                        type: 'server',
                        message: errors[field][0],
                    });
                }
            }

            toast.error(data?.message ?? 'Unable to update ticket.');
            resetFormButton();
        },
    });

    const onSubmit: SubmitHandler<TicketFormData> = data => {
        const submit = isEditing ? updateTicket : createTicket;

        submit(
            toRequest(
                {
                    ...data,
                    pictures:
                        files.length > 0
                            ? (files
                                  .filter(f => f.hasOwnProperty('getMetadata'))
                                  .map(f => ({
                                      id: f.getMetadata('id'),
                                      url: f.getMetadata('url'),
                                      size: f.fileSize,
                                      mime_type: f.file.type,
                                      collection: 'pictures',
                                  })) as unknown as UploadFile[])
                            : pictures,
                },
                event,
            ),
        );
    };

    return (
        <Form.Provider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)} className={'contents'}>
                <Drawer.Header>
                    <Drawer.Title>{drawerTitle} ticket</Drawer.Title>
                    <Drawer.Close />
                </Drawer.Header>
                <Drawer.Body>
                    <div className={'flex flex-col gap-y-6'}>
                        {isEditing && Number(ticket?.sold) > 0 && (
                            <AlertNotification.Root type="info">
                                <AlertNotification.Icon />
                                <AlertNotification.Content>
                                    <AlertNotification.Title>
                                        This ticket has already been purchased by a supporter
                                    </AlertNotification.Title>
                                    <AlertNotification.Description>
                                        Any changes made will not alter previous purchases and will
                                        only reflect on future ticket purchases.
                                    </AlertNotification.Description>
                                </AlertNotification.Content>
                            </AlertNotification.Root>
                        )}
                        <TicketFormHeader eventType={event.type} />
                        <div className={'grid grid-cols-2 gap-4'}>
                            <Form.Container className="col-span-1">
                                <Form.Label name={'price'}>
                                    Price
                                    <Form.LabelAsterisk />
                                </Form.Label>
                                {!isFree ? (
                                    <>
                                        <Form.TextInputRoot
                                            name={'price'}
                                            control={control}
                                            disabled={isFree || bundleOnly}
                                            rules={{
                                                required: isFreeText,
                                            }}
                                            inputRootProps={{
                                                mask: 'decimal',
                                            }}
                                        >
                                            <InputCurrencyIcon />
                                            <Form.TextInputForm placeholder={'100'} />
                                        </Form.TextInputRoot>
                                        <Form.Error name={'price'} />
                                    </>
                                ) : (
                                    <p
                                        className={
                                            'h-10 cursor-not-allowed rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 leading-none text-gray-400'
                                        }
                                    >
                                        Free
                                    </p>
                                )}
                                <div className={'flex items-center gap-x-2'}>
                                    <Form.UseFormCheckboxInput
                                        name={'free'}
                                        disabled={bundleOnly}
                                        control={control}
                                    />
                                    <Form.Label
                                        name={'free'}
                                        className={twMerge(
                                            'cursor-pointer',
                                            bundleOnly && 'cursor-not-allowed text-gray-300',
                                        )}
                                    >
                                        Free
                                    </Form.Label>
                                </div>
                            </Form.Container>

                            {taxDeductibilitySettings && (
                                <Form.Container className="col-span-1">
                                    <Form.Label
                                        name={'retail_price'}
                                        className="flex items-center gap-1"
                                    >
                                        Fair market value
                                        <Tooltip.Root>
                                            <Tooltip.Trigger>
                                                <FontAwesomeIcon icon={faQuestionCircle} />
                                                <span className="sr-only">More info</span>
                                            </Tooltip.Trigger>
                                            <Tooltip.Portal>
                                                <Tooltip.Content inverse>
                                                    <Tooltip.Arrow inverse />
                                                    <p>
                                                        Tickets will only show a tax-deductible
                                                        amount on receipts and end-of-year
                                                        statements if a Fair Market Value is
                                                        provided. The tax-deductible amount will be
                                                        the Ticket Price minus Fair Market Value,
                                                        per IRS guidelines.
                                                    </p>
                                                </Tooltip.Content>
                                            </Tooltip.Portal>
                                        </Tooltip.Root>
                                    </Form.Label>

                                    <Form.TextInputRoot
                                        name={'retail_price'}
                                        control={control}
                                        inputRootProps={{ mask: 'decimal' }}
                                        disabled={isFree}
                                    >
                                        <InputCurrencyIcon />
                                        {!isFree && <Form.TextInputForm placeholder={'0'} />}
                                    </Form.TextInputRoot>
                                    <p className="text-sm text-gray-600">
                                        Actual ticket value for tax purposes.
                                    </p>
                                    <Form.UseFormError name={'retail_price'} />
                                </Form.Container>
                            )}

                            <Form.Container className="col-span-1">
                                <Form.Label name={'total_quantity'}>
                                    Quantity
                                    <Form.LabelAsterisk />
                                </Form.Label>
                                {shouldShowQuantity ? (
                                    <>
                                        <Form.TextInputRoot
                                            name={'total_quantity'}
                                            control={control}
                                            disabled={isUnlimited || bundleOnly}
                                            rules={{
                                                required: quantityRequiredText
                                                    ? 'Must provide a quantity or choose "Unlimited".'
                                                    : false,
                                                min: !bundleOnly
                                                    ? {
                                                          value: 1,
                                                          message:
                                                              'The quantity must be at least 1.',
                                                      }
                                                    : undefined,
                                            }}
                                            inputRootProps={{
                                                mask: 'whole-number',
                                            }}
                                        >
                                            <Form.TextInputForm placeholder={'250'} />
                                        </Form.TextInputRoot>
                                        <Form.Error name={'total_quantity'} />
                                    </>
                                ) : (
                                    <p
                                        className={
                                            'h-10 cursor-not-allowed rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 leading-none text-gray-400'
                                        }
                                    >
                                        Unlimited
                                    </p>
                                )}
                                <div className={'flex items-center gap-x-2'}>
                                    <Form.UseFormCheckboxInput
                                        disabled={bundleOnly}
                                        name={'unlimited'}
                                        control={control}
                                    />
                                    <Form.Label
                                        name={'unlimited'}
                                        className={twMerge(
                                            'cursor-pointer',
                                            bundleOnly && 'cursor-not-allowed text-gray-300',
                                        )}
                                    >
                                        Unlimited
                                    </Form.Label>
                                </div>
                            </Form.Container>
                        </div>

                        <div className={'flex flex-col gap-y-4'}>
                            <div className={'flex justify-between gap-y-1'}>
                                <div className={'flex flex-col gap-y-1'}>
                                    <Form.Label name={'active'}>Available for purchase</Form.Label>
                                    <p className={'text-sm leading-5 text-gray-600'}>
                                        This ticket type is available for purchase on your campaign.
                                        <br />
                                        Disabling makes the ticket unavailable for purchase.
                                    </p>
                                </div>
                                <Form.Switch name={'active'} control={control} />
                            </div>

                            <div className={'flex items-start gap-2'}>
                                <div className="flex h-auto">
                                    <Form.UseFormCheckboxInput
                                        control={control}
                                        name={'bundleOnly'}
                                        onCheckedChange={() => {
                                            const isQuantityAvailable =
                                                unmaskNumber(totalQuantity ?? '0') > 0;
                                            const isBundleOnlyDialog =
                                                !bundleOnly && (isUnlimited || isQuantityAvailable);

                                            if (isBundleOnlyDialog) {
                                                setIsBundleOnlyDialogOpen(true);
                                                return;
                                            }

                                            if (!ticket?.total_quantity) {
                                                setValue('unlimited', false);
                                                setValue('free', false);
                                            }

                                            const totalQuantityValue = ticket?.total_quantity
                                                ? maskWholeNumber(ticket.total_quantity)
                                                : '';
                                            setValue('total_quantity', totalQuantityValue);
                                            setValue('bundleOnly', !bundleOnly);
                                        }}
                                    />
                                </div>
                                <Form.Container className={'gap-1'}>
                                    <Form.Label
                                        className={'leading-none'}
                                        name={path('bundleOnly')}
                                    >
                                        Only available in bundle
                                    </Form.Label>
                                    <p className={'text-sm text-gray-700'}>
                                        This ticket can only be purchased as part of a bundle, and
                                        will be hidden unless added to a bundle that is available
                                        for purchase.
                                    </p>
                                </Form.Container>
                            </div>
                            <div className={'flex items-start gap-2'}>
                                <div className="flex h-auto">
                                    <Form.UseFormCheckboxInput
                                        control={control}
                                        name={'registrationFundraisingOnly'}
                                    />
                                </div>
                                <Form.Container className={'gap-1'}>
                                    <Form.Label
                                        className={'leading-none'}
                                        name={path('registrationFundraisingOnly')}
                                    >
                                        Only available to registration fundraisers
                                    </Form.Label>
                                    <p className={'text-sm text-gray-700'}>
                                        This ticket will only be available to supporters who are
                                        creating a registration fundraising page.
                                    </p>
                                </Form.Container>
                            </div>
                        </div>
                        <div className={'col-span-1 flex flex-col gap-y-2'}>
                            <Form.Label name={'description'}>Description</Form.Label>
                            <Wysiwyg.Root>
                                <UseFormWysiwyg
                                    name={'description'}
                                    control={control}
                                    inline={false}
                                    init={{
                                        min_height: EDITOR_MIN_HEIGHT,
                                        statusbar: false,
                                        placeholder: 'Enter a description...',
                                        plugins: [
                                            ...(aiEnabled ? ['ai'] : []),
                                            ...Wysiwyg.Constants.DESCRIPTION_EDITOR_PLUGINS,
                                            'autoresize',
                                        ],
                                        toolbar: `aidialog aishortcuts ${Wysiwyg.Constants.DESCRIPTION_EDITOR_TOOLBAR}`,
                                        content_style: Wysiwyg.Constants.APP_FONT,
                                        menubar: false,
                                        link_target_list: false,
                                        link_assume_external_targets: true,
                                    }}
                                />
                            </Wysiwyg.Root>
                            <Form.UseFormError name={'description'} />
                        </div>

                        <Form.Container>
                            <Form.Label name={'pictures'}>Upload an image</Form.Label>
                            <div className="flex gap-6">
                                {!arePicturesEmpty && (
                                    <div className="flex flex-col gap-3">
                                        <div>
                                            <img
                                                src={pictures[0].url}
                                                className="max-h-[10rem] max-w-[13.75rem] rounded-lg"
                                                alt="campaign item"
                                            />
                                        </div>
                                        <div className="flex gap-3">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                color="neutral"
                                                onClick={() => ref.current?.browse()}
                                            >
                                                <FontAwesomeIcon icon={faRefresh} />
                                                Replace
                                            </Button>

                                            <Button
                                                size="sm"
                                                variant="outline"
                                                color="error"
                                                onClick={() => setValue('pictures', [])}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                                Delete
                                            </Button>
                                        </div>
                                    </div>
                                )}
                                <div className={twMerge('w-full', !arePicturesEmpty && 'hidden')}>
                                    <Upload
                                        className={'min-h-[12rem]'}
                                        ref={ref}
                                        acceptedFileTypes={['image/*']}
                                        allowReorder={false}
                                        allowReplace={true}
                                        allowMultiple={false}
                                        maxFiles={1}
                                        onupdatefiles={files => {
                                            setFiles(files);
                                        }}
                                        onaddfile={() => {
                                            setValue('pictures', []);
                                        }}
                                    />
                                    <Form.UseFormError name={path('pictures')} />
                                </div>
                            </div>
                        </Form.Container>

                        {hasTicketCustomFields && (
                            <>
                                <Separator className={`-mx-8 !w-auto`} />
                                <TicketCustomFieldForm.Root>
                                    <TicketCustomFieldForm.Header />
                                    <TicketCustomFieldForm.Items />
                                </TicketCustomFieldForm.Root>
                            </>
                        )}
                        <Separator className={'-mx-8 !w-auto'} />
                        {ticket?.is_bundle_item && <Separator className={'-mx-8 !w-auto'} />}
                        <h2 className={'text-base font-medium'}>Add to a bundle</h2>
                        <CampaignItemForm.Root ticket={ticket} totalItems={totalBundles}>
                            <CampaignItemForm.SelectItemHeader />
                            {!isEventBundlesPending && (
                                <Suspense fallback={<Loader />}>
                                    {shouldShowBundleInfo ? (
                                        <CampaignItemForm.SelectItem />
                                    ) : (
                                        <Button
                                            onClick={() => setShouldShowBundleInfo(true)}
                                            size="sm"
                                            variant="outline"
                                            color="neutral"
                                        >
                                            <FontAwesomeIcon icon={faPlus} />
                                            Add this ticket to a bundle
                                        </Button>
                                    )}
                                    {shouldShowBundleInfo && <CampaignItemForm.SelectSummary />}
                                </Suspense>
                            )}
                        </CampaignItemForm.Root>
                    </div>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel asChild={true}>
                            <Button variant="transparent">Cancel</Button>
                        </Drawer.Cancel>
                        <div className={'flex flex-wrap-reverse items-center gap-4'}>
                            {!isEditing && (
                                <Form.SubmitButton
                                    variant={'outline'}
                                    noAnimation={true}
                                    onClick={() => setShouldExitAfterCreate(false)}
                                    isSubmitting={isCreating}
                                >
                                    Create and add another
                                </Form.SubmitButton>
                            )}
                            <Form.SubmitButton
                                onClick={() => setShouldExitAfterCreate(true)}
                                isSubmitting={isCreating || isUpdating}
                            >
                                {isEditing ? 'Save edits' : 'Create'}
                            </Form.SubmitButton>
                        </div>
                    </div>
                </Drawer.Footer>
            </form>
            <BundleOnlyConfirmationDialog.Root
                open={isBundleOnlyDialogOpen}
                onConfirm={() => {
                    setValue('unlimited', false);
                    setValue('total_quantity', '');
                    setIsBundleOnlyDialogOpen(false);
                }}
                onCancel={() => {
                    setValue('bundleOnly', false);
                    setValue(
                        'total_quantity',
                        ticket?.total_quantity ? maskWholeNumber(ticket?.total_quantity) : '',
                    );
                    setIsBundleOnlyDialogOpen(false);
                }}
            />
        </Form.Provider>
    );
};

const defaultForm = (
    eventType: CampaignEvent['type'],
    ticket: CampaignItemBundle | null,
    customFields: CustomField[],
) => {
    if (isEmpty(ticket)) {
        return {
            name: '',
            active: true,
            subtype: eventType === 'hybrid' ? 'physical' : eventType,
            price: '',
            retail_price: '',
            free: false,
            total_quantity: '',
            unlimited: false,
            description: '',
            bundleOnly: false,
            registrationFundraisingOnly: false,
            custom_fields: customFields.map(toCustomFieldFormValue),
            pictures: [],
            bundles: [CampaignItemForm.emptyItemField],
        };
    }

    return {
        name: ticket.name,
        subtype:
            (ticket.subtype as 'physical' | 'digital') ||
            (eventType === 'hybrid' ? 'physical' : ''),
        active: ticket.active,
        price: maskDecimal(ticket.price ?? ''),
        retail_price: maskDecimal(ticket.retail_price ?? ''),
        free: Number(ticket.price) === 0,
        total_quantity:
            ticket.total_quantity === null ? '' : maskWholeNumber(ticket.total_quantity),
        unlimited: ticket.total_quantity === null,
        description: ticket.description || '',
        bundleOnly: ticket.bundle_only,
        registrationFundraisingOnly: ticket.scope === 'registrant',
        custom_fields: ticket.custom_fields?.map(toCustomFieldFormValue) || [],
        pictures: ticket?.pictures as unknown as UploadFile[],
        bundles: ticket?.item_bundles?.length
            ? ticket.item_bundles?.map(item => {
                  return {
                      value: item.name ?? '',
                      id: item.id.toString(),
                      quantity: item.quantity_per_bundle?.toString(),
                      order: item.bundle_order,
                      total_quantity: item.total_quantity,
                      remaining: item.remaining,
                  };
              })
            : [CampaignItemForm.emptyItemField],
    };
};

const toRequest = (form: TicketFormData, event: CampaignEvent) => {
    let price = null;

    if (form.free || form.bundleOnly) {
        price = 0;
    } else if (form.price) {
        price = unmaskNumber(form.price);
    }

    return {
        name: form.name,
        subtype: event.type === 'hybrid' ? form.subtype : event.type,
        active: form.active,
        price,
        retail_price: form.retail_price && !form.free ? unmaskNumber(form.retail_price) : null,
        total_quantity:
            form.unlimited || form.bundleOnly ? null : unmaskNumber(form.total_quantity),
        description: form.description,
        bundle_only: form.bundleOnly,
        scope: form.registrationFundraisingOnly ? 'registrant' : null,
        bundles: form.bundles[0].quantity
            ? CampaignItemForm.toCampaignItemsRequest(form.bundles)
            : [],
        custom_fields: form.custom_fields?.map(toCustomFieldRequestValue),
        pictures: form.pictures as unknown as MediaUpload[],
    };
};

const toCustomFieldFormValue = (customField: CustomField) => ({
    id: customField?.id,
    title: customField.title,
    description: customField.description ?? '',
    type: customField.type,
    required: customField.required,
    hideField: !customField.public,
    defaultResponse: customField.default_value ?? '',
    answers: customField?.answers ? CustomFieldForm.toFieldArray(customField.answers) : null,
    all_tickets: customField.group === 'ticket',
});

const toCustomFieldRequestValue = (form: TicketCustomFieldFormValues) => ({
    id: form?.id && form.id >= 0 ? form.id : undefined,
    title: form.title,
    description: form.description ? form.description : null,
    type: form.type,
    required: form.required ?? false,
    public: !form.hideField,
    default_value: form.defaultResponse,
    answers:
        CustomFieldForm.typeHasAnswers(form.type) &&
        form.answers &&
        CustomFieldForm.toFlatArray(form.answers).length
            ? CustomFieldForm.toFlatArray(form.answers)
            : null,
    group: form.all_tickets ? 'ticket' : null,
});

export { TicketFormDrawer };
